import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { tutorApi, TUTOR_TOKEN_KEY } from "../lib/tutorApi";

const TutorAuthContext = createContext(null);

export function TutorAuthProvider({ children }) {
    // tutor: null = still checking, false = unauthenticated, object = authenticated
    const [tutor, setTutor] = useState(null);
    const [portalEnabled, setPortalEnabled] = useState(null); // null = checking, true|false

    const fetchPortalStatus = useCallback(async () => {
        try {
            const { data } = await tutorApi.get("/tutor/portal/status");
            setPortalEnabled(!!data?.enabled);
            return !!data?.enabled;
        } catch {
            setPortalEnabled(false);
            return false;
        }
    }, []);

    const fetchMe = useCallback(async () => {
        const token = localStorage.getItem(TUTOR_TOKEN_KEY);
        if (!token) {
            setTutor(false);
            return;
        }
        try {
            const { data } = await tutorApi.get("/tutor/auth/me");
            setTutor(data?.tutor || false);
        } catch {
            localStorage.removeItem(TUTOR_TOKEN_KEY);
            setTutor(false);
        }
    }, []);

    useEffect(() => {
        (async () => {
            const enabled = await fetchPortalStatus();
            if (enabled) await fetchMe();
            else setTutor(false);
        })();
    }, [fetchPortalStatus, fetchMe]);

    const login = async (email, password) => {
        const { data } = await tutorApi.post("/tutor/auth/login", { email, password });
        localStorage.setItem(TUTOR_TOKEN_KEY, data.access_token);
        setTutor(data.tutor);
        return data.tutor;
    };

    const logout = async () => {
        try { await tutorApi.post("/tutor/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem(TUTOR_TOKEN_KEY);
        setTutor(false);
    };

    const refreshMe = fetchMe;

    return (
        <TutorAuthContext.Provider value={{ tutor, login, logout, refreshMe, portalEnabled, refreshPortalStatus: fetchPortalStatus }}>
            {children}
        </TutorAuthContext.Provider>
    );
}

export function useTutorAuth() {
    const ctx = useContext(TutorAuthContext);
    if (!ctx) throw new Error("useTutorAuth must be used within TutorAuthProvider");
    return ctx;
}
